/**
 * MeetingDetailPage — volwaardige pagina voor één meeting (/MeetingDashboard/:id).
 * Vervangt de popup vanuit het overzicht: alle schermruimte voor transcript +
 * werkpanelen, en een deelbare URL per meeting.
 *
 * Linkerkolom = je meeting-lijst (vervangt hier de secundaire nav — zelfde
 * design-principe als de gesprekken-kolom in de Offerte Generator).
 */
import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, CircularProgress, Alert, Button, Typography, Stack } from '@mui/material';
import { useMsal } from '@azure/msal-react';
import toast from 'react-hot-toast';
import { bevestig } from '../../utils/bevestig';
import { AI_ENGINE_URL } from '../../utils/apiConfig';
import { authFetch } from '../../utils/authHelpers';
import { parseUtcDate } from '../../utils/utcDate';
import MeetingDetail from './MeetingDetail';

const STATUS_STYLE = {
  new: { label: 'Nieuw', color: '#0369a1', bg: '#e0f2fe' },
  review_needed: { label: 'Review', color: '#7c3aed', bg: '#ede9fe' },
  completed: { label: 'Afgerond', color: '#15803d', bg: '#dcfce7' },
};

// Module-niveau component (niet inline in de page — dat zou per render remounten).
function MeetingListRail({ meetings, activeId, onPick, onBack, onDelete, onOpruimen }) {
  return (
    <Box sx={{ width: 292, flexShrink: 0, display: { xs: 'none', lg: 'flex' }, flexDirection: 'column',
      borderRight: '1px solid #eceff1', bgcolor: '#fafbfc', px: 1.5, py: 2, overflowY: 'auto',
      height: 'calc(100vh - 44px)' }}>
      <Stack direction="row" spacing={0.75} alignItems="center" sx={{ px: 0.5, mb: 1.25 }}>
        <Typography sx={{ fontSize: 12, fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#64748b' }}>
          Meetings
        </Typography>
      </Stack>
      <Stack direction="row" alignItems="center" sx={{ mb: 1 }}>
        <Button size="small" onClick={onBack} sx={{ justifyContent: 'flex-start', textTransform: 'none', fontWeight: 700, color: '#526075' }}>
          ← Alle meetings
        </Button>
        <Box sx={{ flex: 1 }} />
        <Button size="small" onClick={onOpruimen}
          sx={{ textTransform: 'none', fontWeight: 700, color: '#94a3b8', minWidth: 0, '&:hover': { color: '#b91c1c' } }}>
          🗑 Opruimen…
        </Button>
      </Stack>
      <Stack spacing={0.25}>
        {meetings.map((m) => {
          const active = String(m.id) === String(activeId);
          const st = STATUS_STYLE[m.status] || null;
          return (
            <Box key={m.id} onClick={() => onPick(m.id)} sx={{
              px: 1.25, py: 1, borderRadius: 1.5, cursor: 'pointer', position: 'relative',
              borderLeft: '3px solid', borderColor: active ? '#526075' : 'transparent',
              bgcolor: active ? '#eef2f6' : 'transparent',
              '&:hover': { bgcolor: active ? '#eef2f6' : '#f3f4f6' },
              '&:hover .rij-weg': { opacity: 1 },
            }}>
              <Box component="span" className="rij-weg" role="button" aria-label="Meeting verwijderen"
                onClick={(e) => { e.stopPropagation(); onDelete(m); }}
                sx={{
                  position: 'absolute', top: 6, right: 6, opacity: 0, transition: 'opacity .12s',
                  fontSize: 13, lineHeight: 1, px: 0.5, py: 0.25, borderRadius: 1,
                  color: '#94a3b8', '&:hover': { color: '#b91c1c', bgcolor: '#fee2e2' },
                }}>
                ✕
              </Box>
              {/* Titel over max 2 regels — leesbaar i.p.v. afgekapt op één regel */}
              <Typography sx={{
                fontSize: 13.5, fontWeight: active ? 700 : 600, lineHeight: 1.35,
                color: active ? '#2d3335' : '#374151',
                display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
              }}>
                {m.title || 'Meeting'}
              </Typography>
              <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mt: 0.4 }}>
                <Typography sx={{ fontSize: 11.5, color: '#94a3b8' }}>
                  {m.meeting_date ? parseUtcDate(m.meeting_date).toLocaleDateString('nl-NL', { day: 'numeric', month: 'short' }) : ''}
                </Typography>
                {st && (
                  <Typography sx={{ fontSize: 10, fontWeight: 800, color: st.color, bgcolor: st.bg, px: 0.75, py: 0.15, borderRadius: '9999px' }}>
                    {st.label}
                  </Typography>
                )}
              </Stack>
            </Box>
          );
        })}
      </Stack>
    </Box>
  );
}

export default function MeetingDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { accounts } = useMsal();
  const userEmail = accounts?.[0]?.username || '';
  const [meeting, setMeeting] = useState(null);
  const [meetings, setMeetings] = useState([]);
  const [error, setError] = useState(null);

  const fetchMeeting = useCallback(async () => {
    try {
      const res = await authFetch(`${AI_ENGINE_URL}/api/v1/meetings/${id}`);
      if (!res.ok) throw new Error(res.status === 404 ? 'Meeting niet gevonden' : `HTTP ${res.status}`);
      setMeeting(await res.json());
    } catch (e) { setError(e.message); }
  }, [id]);

  useEffect(() => { setMeeting(null); setError(null); fetchMeeting(); }, [fetchMeeting]);

  // Lijst voor de linkerkolom (eigen meetings, zoals het dashboard filtert).
  useEffect(() => {
    let active = true;
    const params = userEmail ? `?user_email=${encodeURIComponent(userEmail)}&limit=30` : '?limit=30';
    authFetch(`${AI_ENGINE_URL}/api/v1/meetings/${params}`)
      .then((r) => (r.ok ? r.json() : []))
      .then((d) => { if (active) setMeetings(Array.isArray(d) ? d : []); })
      .catch(() => {});
    return () => { active = false; };
  }, [userEmail]);

  const handleDelete = async (meetingId) => {
    try {
      const res = await authFetch(`${AI_ENGINE_URL}/api/v1/meetings/${meetingId}`, { method: 'DELETE' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      toast.success('Meeting verwijderd');
      navigate('/MeetingDashboard');
    } catch { toast.error('Kon meeting niet verwijderen'); }
  };

  // Verwijderen vanuit de linkerkolom: bevestigen (geen undo-window hier),
  // dan weg uit de lijst; de open meeting zelf weg = terug naar het overzicht.
  const verwijderUitLijst = async (m) => {
    const wanneer = m.meeting_date ? ` (${parseUtcDate(m.meeting_date).toLocaleDateString('nl-NL', { day: 'numeric', month: 'short' })})` : '';
    if (!(await bevestig({ titel: `"${m.title || 'Meeting'}"${wanneer} verwijderen?`, tekst: 'Definitief — dit kan niet ongedaan worden gemaakt.', knop: 'Verwijderen', gevaarlijk: true }))) return;
    try {
      const res = await authFetch(`${AI_ENGINE_URL}/api/v1/meetings/${m.id}`, { method: 'DELETE' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setMeetings((prev) => prev.filter((x) => x.id !== m.id));
      toast.success('Meeting verwijderd');
      if (String(m.id) === String(id)) navigate('/MeetingDashboard');
    } catch { toast.error('Kon meeting niet verwijderen'); }
  };

  // Bulk-opruimen op exacte titel ("alle dagstarts weg" — Joris, 26 aug).
  // Prompt toont de titels met aantallen; verwijderen gaat één voor één via
  // hetzelfde DELETE-endpoint, met de teller in een toast.
  const opruimen = async () => {
    const perTitel = new Map();
    meetings.forEach((m) => {
      const t = (m.title || 'Meeting').trim();
      perTitel.set(t, (perTitel.get(t) || 0) + 1);
    });
    const groepen = [...perTitel.entries()].filter(([, n]) => n >= 2).sort((a, b) => b[1] - a[1]);
    if (!groepen.length) { toast('Geen herhaalde titels in je lijst om op te ruimen.'); return; }
    const overzicht = groepen.slice(0, 8).map(([t, n]) => `• ${t} (${n}×)`).join('\n');
    const invoer = window.prompt(
      `Alle meetings met precies deze titel verwijderen. Herhaalde titels in je lijst:\n${overzicht}\n\nTyp de titel exact over om te bevestigen:`,
      groepen[0][0]
    );
    if (invoer === null) return;
    const titel = invoer.trim();
    const doelen = meetings.filter((m) => (m.title || 'Meeting').trim() === titel);
    if (!doelen.length) { toast.error(`Geen meetings met titel "${titel}" in je lijst.`); return; }
    if (!(await bevestig({ titel: `${doelen.length} meeting${doelen.length === 1 ? '' : 's'} verwijderen?`, tekst: `Alle meetings met titel "${titel}" worden definitief verwijderd.`, knop: 'Verwijderen', gevaarlijk: true }))) return;
    const toastId = toast.loading(`Verwijderen… 0/${doelen.length}`);
    let gelukt = 0;
    for (const m of doelen) {
      try {
        const res = await authFetch(`${AI_ENGINE_URL}/api/v1/meetings/${m.id}`, { method: 'DELETE' });
        if (res.ok) {
          gelukt += 1;
          setMeetings((prev) => prev.filter((x) => x.id !== m.id));
        }
      } catch { /* teller vertelt het eerlijke verhaal */ }
      toast.loading(`Verwijderen… ${gelukt}/${doelen.length}`, { id: toastId });
    }
    toast.dismiss(toastId);
    (gelukt === doelen.length ? toast.success : toast.error)(
      `${gelukt}/${doelen.length} verwijderd${gelukt < doelen.length ? ' — de rest lukte niet, probeer opnieuw' : ''}`
    );
    if (doelen.some((m) => String(m.id) === String(id))) navigate('/MeetingDashboard');
  };

  return (
    <Box sx={{ display: 'flex', width: '100%', minWidth: 0 }}>
      <MeetingListRail meetings={meetings} activeId={id}
        onPick={(mid) => navigate(`/MeetingDashboard/${mid}`)}
        onBack={() => navigate('/MeetingDashboard')}
        onDelete={verwijderUitLijst}
        onOpruimen={opruimen} />
      {error ? (
        <Box sx={{ flex: 1, maxWidth: 700, mx: 'auto', pt: 6, px: 3 }}>
          <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
          <Button variant="outlined" onClick={() => navigate('/MeetingDashboard')}>← Terug naar Meetings</Button>
        </Box>
      ) : !meeting ? (
        <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center', pt: 10 }}><CircularProgress /></Box>
      ) : (
        <MeetingDetail
          key={meeting.id} // schone state bij wisselen via de linkerkolom
          variant="page"
          open
          meeting={meeting}
          onClose={() => navigate('/MeetingDashboard')}
          onUpdate={fetchMeeting}
          onDelete={handleDelete}
        />
      )}
    </Box>
  );
}
